import React, { memo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Badge
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { Info as InfoIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

/**
 * ItemCard Component - Displays a Splinterlands item in the market
 * 
 * @param {Object} props - Component props
 * @param {Object} props.item - The item data from the market landing API
 * @param {Function} props.onItemClick - Callback when item is clicked
 * @param {Function} props.onBuyClick - Callback when buy button is clicked
 * @returns {JSX.Element} - The rendered item card component
 */
const ItemCard = memo(({ item, onItemClick, onBuyClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset image states when item changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [item?.detailImage]);

  if (!item) return null;

  // Get the asset type for styling
  const getAssetTypeColor = () => {
    switch (item.assetName) {
      case 'PACKS':
        return '#4CAF50'; // Green
      case 'LAND':
        return '#8BC34A'; // Light Green
      case 'TOTEMS':
        return '#FF9800'; // Orange
      case 'TITLES':
        return '#9C27B0'; // Purple
      case 'DEEDS':
        return '#795548'; // Brown
      case 'RESOURCES':
        return '#00BCD4'; // Cyan
      case 'TOTEM_ITEMS':
        return '#FF5722'; // Deep Orange
      case 'TOTEM_FRAGMENTS':
        return '#FFC107'; // Amber
      case 'AVATARS':
        return '#E91E63'; // Pink
      case 'CONSUMABLES':
        return '#3F51B5'; // Indigo
      default:
        return colors.grey[400];
    }
  };

  // Get rarity color for items that have rarity (like totems)
  const getRarityColor = () => {
    const name = item.detailName?.toLowerCase() || '';
    if (name.includes('common')) return '#9E9E9E';
    if (name.includes('rare')) return '#2196F3';
    if (name.includes('epic')) return '#9C27B0';
    if (name.includes('legendary')) return '#FF9800';
    return null;
  };

  const assetTypeColor = getAssetTypeColor();
  const rarityColor = getRarityColor();
  const borderColor = rarityColor || assetTypeColor;

  // Format price
  const formatPrice = (price) => {
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(1)}k`;
    }
    return `$${price.toFixed(3)}`;
  };

  const minPrice = item.prices && item.prices.length > 0 ? item.prices[0].minPrice : 0;

  return (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: 'pointer',
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${colors.primary[300]}`,
        boxShadow: isHovered 
          ? `0 12px 28px rgba(0,0,0,0.25), 0 0 0 1px ${colors.primary[200]}` 
          : "0 2px 8px rgba(0,0,0,0.1)",
        transform: isHovered ? "translateY(-4px)" : "none",
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        '&:hover': {
          '& .item-image': {
            transform: 'scale(1.02)'
          }
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onItemClick && onItemClick(item)}
    >
      {/* Asset Type Badge */}
      {item.assetDescription && (
        <Box 
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 3
          }}
        >
          <Chip
            label={item.assetDescription}
            size="small"
            sx={{
              backgroundColor: `${assetTypeColor}20`,
              color: assetTypeColor,
              fontWeight: 'bold',
              fontSize: '0.7rem',
              height: '24px',
              textTransform: 'uppercase',
              border: `1px solid ${assetTypeColor}40`,
              backdropFilter: 'blur(10px)'
            }}
          />
        </Box>
      )}

      {/* Listed Count Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 3
        }}
      >
        <Chip
          label={`${item.numListed}`}
          size="small"
          sx={{
            backgroundColor: `${colors.grey[100]}15`,
            color: colors.grey[100],
            fontWeight: 'bold',
            fontSize: '0.7rem',
            height: '24px',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${colors.grey[100]}20`
          }}
        />
      </Box>

      {/* Item Image */}
      <Box 
        sx={{ 
          pt: 3, 
          px: 3, 
          pb: 2,
          position: "relative",
          flex: "1 0 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Box 
          className="item-image"
          sx={{ 
            height: 180, 
            width: "100%",
            position: "relative",
            borderRadius: "12px",
            overflow: "hidden",
            transition: "transform 0.3s ease",
            backgroundColor: `${colors.primary[500]}80`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}
        >
          {item.detailImage && !imageError ? (
            <img
              src={item.detailImage}
              alt={item.detailName}
              style={{
                maxWidth: '85%',
                maxHeight: '85%',
                objectFit: 'contain',
                borderRadius: '8px',
                opacity: imageLoaded ? 1 : 0.7,
                transition: 'opacity 0.3s ease'
              }}
              onLoad={() => {
                setImageLoaded(true);
                setImageError(false);
              }}
              onError={(e) => {
                console.warn(`Failed to load image for ${item.detailName}:`, item.detailImage);
                setImageError(true);
                setImageLoaded(false);
              }}
            />
          ) : null}
          
          {/* Fallback content only when image fails to load */}
          {(!item.detailImage || imageError) && (
            <Box
              sx={{
                width: 120,
                height: 120,
                background: `linear-gradient(135deg, ${assetTypeColor}40, ${assetTypeColor}20)`,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${assetTypeColor}30`,
                flexDirection: 'column',
                gap: 1
              }}
            >
              {/* Icon representation for different asset types */}
              {item.assetName === 'LAND' && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: assetTypeColor,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    {item.detailId === 'TRACT' ? '🏞️' : 
                     item.detailId === 'REGION' ? '🏰' : 
                     item.detailId === 'PLOT' ? '🌱' : '🗺️'}
                  </Typography>
                </Box>
              )}
              {item.assetName === 'PACKS' && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: assetTypeColor,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    📦
                  </Typography>
                </Box>
              )}
              {item.assetName === 'TOTEMS' && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: assetTypeColor,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    🗿
                  </Typography>
                </Box>
              )}
              {item.assetName === 'CONSUMABLES' && (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: assetTypeColor,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}
                  >
                    🧪
                  </Typography>
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{
                  color: assetTypeColor,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  maxWidth: '90%'
                }}
              >
                {item.detailName || item.assetName}
              </Typography>
              {imageError && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey[400],
                    fontSize: '0.7rem',
                    textAlign: 'center'
                  }}
                >
                  Image unavailable
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <CardContent sx={{ p: 2, pb: '8px !important' }}>
        {/* Item Name */}
        <Typography
          variant="h6"
          sx={{
            color: colors.grey[100],
            fontWeight: 'bold',
            fontSize: '1rem',
            mb: 1,
            textAlign: 'center',
            minHeight: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {item.detailName}
        </Typography>

        {/* Circulation Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
            Circulation:
          </Typography>
          <Typography variant="body2" sx={{ color: colors.grey[100] }}>
            {item.numCirculation?.toLocaleString() || 'N/A'}
          </Typography>
        </Box>

        {/* Price Info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
            Min Price:
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.greenAccent[400],
              fontWeight: 'bold'
            }}
          >
            {minPrice > 0 ? formatPrice(minPrice) : 'N/A'}
          </Typography>
        </Box>

        {/* Buy Button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onBuyClick && onBuyClick(item);
          }}
          disabled={item.numListed === 0}
          sx={{
            backgroundColor: item.numListed > 0 ? colors.greenAccent[600] : colors.grey[600],
            color: 'white',
            fontWeight: 'bold',
            py: 1.2,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.9rem',
            '&:hover': {
              backgroundColor: item.numListed > 0 ? colors.greenAccent[700] : colors.grey[600],
              transform: item.numListed > 0 ? 'translateY(-1px)' : 'none',
              boxShadow: item.numListed > 0 ? `0 4px 12px ${colors.greenAccent[600]}40` : 'none'
            },
            '&:disabled': {
              backgroundColor: colors.grey[600],
              color: colors.grey[400]
            }
          }}
        >
          {item.numListed > 0 ? 'View Listings' : 'Sold Out'}
        </Button>

        {/* Info Button */}
        {item.detailDescription && (
          <Tooltip title={item.detailDescription} arrow placement="top">
            <IconButton 
              size="small" 
              sx={{ 
                color: colors.grey[400],
                position: 'absolute',
                top: 50,
                left: 12,
                zIndex: 4,
                backgroundColor: `${colors.grey[900]}80`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${colors.grey[600]}40`,
                width: 28,
                height: 28,
                '&:hover': {
                  color: colors.grey[200],
                  backgroundColor: `${colors.grey[800]}90`
                }
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </CardContent>
    </Card>
  );
});

export default ItemCard;
